/**
 * Shipping hooks (S6.1) — house list stack + store-first mutations for the
 * 出貨通知 / 裝櫃批次 pages split out of the codex ShippingPages seam.
 *
 * - `useShippingNoticesTable` / `useShipmentBatchesTable` drive the two list
 *   pages via useAntdTable (pre-first-fetch `loading || params.length === 0`
 *   guard, store echo). The mock list endpoints take no params, so search /
 *   status filter client-side at mock scale (GoodsReceiptPage precedent).
 * - `useShippingNoticeMutations` is the 白單/黃單 flow: create → issue 白單
 *   (send) → capture the factory 黃單 counter-sign (acknowledge) which feeds
 *   the A2 出貨通知再確認 buckets server-side.
 * - `useShipmentBatchMutations` fronts the salvaged batch gates: cut-off
 *   (櫃號+seal required, qtyLoaded ≤ planned, short-load → owned exception)
 *   and depart (requires cut_off). Loaded qty is FIXED at seal.
 * - Reads use `/api/`-prefixed static cacheKeys; every mutation updates the
 *   Zustand echo first, then invalidates via exact-key clears.
 */

import { useAntdTable, useRequest } from 'ahooks';
import { purchaseOrderApi, shippingApi } from '@/services/api';
import type {
  CreateShipmentBatchRequest,
  CreateShippingNoticeRequest,
  CounterSignShippingNoticeLines,
  CutOffShipmentBatchRequest,
} from '@/services/api/shipping.api';
import { useShippingStore } from '@/stores/shippingStore';
import type { BatchFilters, NoticeFilters } from '@/stores/shippingStore';
import type { TableHookResult } from '@/hooks/useListPage';
import { clearCacheWhere } from '@/utils/cacheKeyRegistry';
import type { PurchaseOrder } from '@/types/api/purchaseOrder';
import type { ShipmentBatch, ShippingNotice } from '@/types/api/shipping';

// ---------------------------------------------------------------------------
// Cache keys (static, /api/-prefixed — house rule)
// ---------------------------------------------------------------------------

const BATCH_OPTIONS_CACHE_KEY = '/api/shipment-batches?view=shipping-options';
const PO_OPTIONS_CACHE_KEY = '/api/purchase-orders?view=shipping-options';

/** Mock-scale ceiling for the client-side option/composition fetches. */
const OPTIONS_FETCH_PAGE_SIZE = 500;

/**
 * Invalidate every cache a shipping mutation can stale: both shipping lists,
 * the OCC projections (send/acknowledge/cut-off mutate quantity buckets and
 * can raise 訂單異常), and the 進貨 prefill that reads seal-fixed loads.
 */
export function invalidateShippingCaches() {
  clearCacheWhere(
    (key) =>
      key.startsWith('/api/shipping-notices') ||
      key.startsWith('/api/shipment-batches') ||
      key.startsWith('/api/occ') ||
      key.startsWith('/api/goods-receipts')
  );
}

// ---------------------------------------------------------------------------
// Lists (useAntdTable — S0.3 list-kit contract)
// ---------------------------------------------------------------------------

export function useShippingNoticesTable(filters: NoticeFilters): TableHookResult<ShippingNotice> {
  const { setNotices } = useShippingStore();

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const response = await shippingApi.listNotices();
      const term = filters.search.trim().toLowerCase();
      const rows = response.data.filter((notice) => {
        if (filters.status !== 'all' && notice.status !== filters.status) return false;
        if (!term) return true;
        return [notice.noticeNumber, notice.vendorName].join(' ').toLowerCase().includes(term);
      });
      const start = (current - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      setNotices(pageRows);
      return { total: rows.length, list: pageRows };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.search, filters.status],
    }
  );

  return {
    tableProps,
    refresh,
    // Pre-first-fetch guard (house rule) — never flash the empty state
    isLoading: loading || params.length === 0,
    error: error ?? null,
  };
}

export function useShipmentBatchesTable(filters: BatchFilters): TableHookResult<ShipmentBatch> {
  const { setBatches } = useShippingStore();

  const { tableProps, refresh, loading, error, params } = useAntdTable(
    async ({ current, pageSize }) => {
      const response = await shippingApi.listBatches();
      const term = filters.search.trim().toLowerCase();
      const rows = response.data.filter((batch) => {
        if (filters.status !== 'all' && batch.status !== filters.status) return false;
        if (!term) return true;
        return [batch.batchNumber, batch.vesselOrCarrier, batch.containerNumber ?? '']
          .join(' ')
          .toLowerCase()
          .includes(term);
      });
      const start = (current - 1) * pageSize;
      const pageRows = rows.slice(start, start + pageSize);
      setBatches(pageRows);
      return { total: rows.length, list: pageRows };
    },
    {
      defaultPageSize: 10,
      refreshDeps: [filters.search, filters.status],
    }
  );

  return {
    tableProps,
    refresh,
    isLoading: loading || params.length === 0,
    error: error ?? null,
  };
}

// ---------------------------------------------------------------------------
// Options (notice create modal joins batch → PO → vendor; batch lookups)
// ---------------------------------------------------------------------------

/** All batches for option pickers + batchNumber lookups (shared cacheKey). */
export function useShipmentBatchOptions() {
  const { data, error, loading } = useRequest(() => shippingApi.listBatches(), {
    cacheKey: BATCH_OPTIONS_CACHE_KEY,
  });
  return { batches: data?.data ?? [], error, isLoading: loading };
}

/** Mock-scale PO fetch — resolves batch poIds → vendor + line display data. */
export function useShippingPoOptions() {
  const { data, error, loading } = useRequest(
    async () => {
      const response = await purchaseOrderApi.list({
        page: 1,
        pageSize: OPTIONS_FETCH_PAGE_SIZE,
      });
      return response.data;
    },
    { cacheKey: PO_OPTIONS_CACHE_KEY }
  );
  return { purchaseOrders: (data ?? []) as PurchaseOrder[], error, isLoading: loading };
}

// ---------------------------------------------------------------------------
// Mutations (store-first: update Zustand echo + exact-key clearCache)
// ---------------------------------------------------------------------------

export function useShippingNoticeMutations() {
  const { upsertNoticeInList } = useShippingStore();

  const createNotice = async (body: CreateShippingNoticeRequest): Promise<ShippingNotice> => {
    const response = await shippingApi.createNotice(body);
    upsertNoticeInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  /** 發出白單 — issue the notice to the factory. */
  const issueNotice = async (id: string): Promise<ShippingNotice> => {
    const response = await shippingApi.sendNotice(id);
    upsertNoticeInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  /** 黃單回簽 capture — feeds the A2 出貨通知再確認 buckets server-side. */
  const counterSignNotice = async (
    id: string,
    lines: CounterSignShippingNoticeLines
  ): Promise<ShippingNotice> => {
    const response = await shippingApi.acknowledgeNotice(id, lines);
    upsertNoticeInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  return { createNotice, issueNotice, counterSignNotice };
}

export function useShipmentBatchMutations() {
  const { upsertBatchInList } = useShippingStore();

  const createBatch = async (body: CreateShipmentBatchRequest): Promise<ShipmentBatch> => {
    const response = await shippingApi.createBatch(body);
    upsertBatchInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  /** 封櫃截單 — server gates 櫃號+seal and qtyLoaded ≤ planned; short-load
   *  raises the owned 訂單異常. Loaded qty is fixed from here on. */
  const cutOffBatch = async (
    id: string,
    body: CutOffShipmentBatchRequest
  ): Promise<ShipmentBatch> => {
    const response = await shippingApi.cutOffBatch(id, body);
    upsertBatchInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  /** 離港 — server 409s unless already cut off. */
  const departBatch = async (id: string): Promise<ShipmentBatch> => {
    const response = await shippingApi.departBatch(id);
    upsertBatchInList(response.data);
    invalidateShippingCaches();
    return response.data;
  };

  return { createBatch, cutOffBatch, departBatch };
}
